import { Fragment, useState } from 'react';

import Loader from '../../../../components/Common/Loader';
import CytoscapeChart from './CytoscapeChart';
import GraphTopContent from '../Common/GraphTopContent';
import { NoRecordsFound, NoGraphsAvailable } from '../../NoRecordsFound';

import { uniqueIdentViewsVariables, getNetworkData, unifyNetworkObj as unifyData } from './NetworkHelpers';

import useNetworkChart from '../../graphHooks/UseNetworkChart';
import { userAppContext } from '../../../../context/Context';
import { useRightSidebarLayout } from '../../../../modules/search/searchGrid/context/RightSidebarLayoutContext';

interface NetworkChartProps {
  source: any;
  keySearch: any;
  view: any;
  names: any;
  chartInfo: any;
  setChartInfo: any;
  baseUrl: any;
  parent: any;
  networkSearchVariables: any;
}

function NetworkChart({
  source,
  keySearch,
  view,
  names,
  chartInfo,
  setChartInfo,
  baseUrl,
  parent,
  networkSearchVariables,
}: NetworkChartProps) {
  // Data necessary for using the custom hook
  const { restrictGridResultCount } = userAppContext();
  const { isSidebarOpen, sidebarWidth } = useRightSidebarLayout();
  const defaultPageSize = restrictGridResultCount || 20;
  const sampleSizeOptions = [20, 50, 100, 250, 'ALL'];
  const [sampleSize, setSampleSize] = useState<number | 'ALL'>(defaultPageSize);
  const requestPageSize = sampleSize === 'ALL' ? -1 : sampleSize;
  const fetchAll = sampleSize === 'ALL';

  // empty nodes and edges arrays as the initial data for the chart
  const initialChartData = { nodes: [], edges: [] };

  // calling the useRequestMultipleFilters hook
  const {
    chartData: networkData,
    isLoading,
    isPayloadEmpty,
    totalResults,
  } = useNetworkChart({
    chartInfo,
    initialChartData,
    keySearch,
    source,
    baseUrl,
    parent,
    pageSize: requestPageSize,
    view,
    getNetworkData,
    unifyData,
    uniqueIdentViewsVariables,
    names,
  });

  // if the nodes array is empty, the chart is empty
  const isChartDataEmpty = networkData.nodes.length === 0 ? true : false;
  const visibleResults = fetchAll
    ? totalResults || requestPageSize
    : Math.min(requestPageSize, totalResults || requestPageSize);

  return (
    <section className="mb-8 pb-8">
      {isLoading && (
        <div className="row">
          {' '}
          <Loader />
        </div>
      )}
      {!isLoading && !isPayloadEmpty && (
        <Fragment>
          {!isChartDataEmpty && (
            <Fragment>
              <GraphTopContent
                names={names}
                view={view}
                graphSearchVariables={networkSearchVariables}
                chartInfo={chartInfo}
                setChartInfo={setChartInfo}
                parent={parent}
                resultsNumber={visibleResults}
                totalResults={totalResults}
                selectedSampleSize={sampleSize}
                onSampleSizeChange={setSampleSize}
                sampleSizeOptions={sampleSizeOptions}
              />
              <section
                className="row network-container justify-center mb-8 pb-8"
                style={{
                  width: isSidebarOpen ? `calc(96vw - ${sidebarWidth}px)` : '96vw',
                }}
              >
                <CytoscapeChart elements={networkData} names={names} parent={parent} chartInfo={chartInfo} />
              </section>
            </Fragment>
          )}
          {isChartDataEmpty && (
            <Fragment>
              <GraphTopContent
                names={names}
                view={view}
                graphSearchVariables={networkSearchVariables}
                chartInfo={chartInfo}
                setChartInfo={setChartInfo}
                parent={parent}
                resultsNumber={0}
                totalResults={totalResults}
                selectedSampleSize={sampleSize}
                onSampleSizeChange={setSampleSize}
                sampleSizeOptions={sampleSizeOptions}
              />
              <NoGraphsAvailable />
            </Fragment>
          )}
        </Fragment>
      )}
      {isPayloadEmpty && !isLoading && (
        <div className="row">
          {' '}
          <NoRecordsFound />
        </div>
      )}
    </section>
  );
}

export default NetworkChart;
